import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { Colors, Typography } from '@b-stock/fe-core/design-system'

import { AccordionCard } from '@/buyer-portal/components/layouts/CardLayout/card/AccordionCard'
import { CardFooterNote } from '@/buyer-portal/components/layouts/CardLayout/card/CardFooterNote'
import { CardBody } from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import { AchProcessingNotice } from '@/components/AchProcessingNotice'

import {
  getPaymentMethods,
  type AvailablePaymentMethodsProps,
} from './paymentMethods'

const Body = styled(CardBody)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const Section = styled.div`
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const SectionTitle = styled.div`
  ${Typography.Subtitle2}
`

const SectionBody = styled.p`
  ${Typography.Body3}
  margin-bottom: 0;
`

const MethodIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  font-size: 0.75rem;
  line-height: 0.75rem;
  margin-right: 0.5rem;
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem;
`

const Chip = styled.span`
  ${Typography.Subtitle2}
  background: ${Colors.Semantic.Neutral.medium_grey};
  border-radius: 0.25rem;
  color: ${Colors.Semantic.Neutral.very_dark_grey};
  padding: 0.25rem 0.5rem;
  white-space: nowrap;
`

// Sits directly under the chips, which already supply the top padding.
const CollapsedNote = styled(AchProcessingNotice)`
  margin: 0 0.75rem 0.75rem;
`

const MethodNote = styled(AchProcessingNotice)`
  margin-top: 0.5rem;
`

export const AvailablePaymentMethodsCard = (
  props: AvailablePaymentMethodsProps
) => {
  const { renderTextMap: t, defaultExpanded = false, children } = props
  const methods = getPaymentMethods(props)

  // Composed into both the expanded body and the collapsed preview so it stays
  // visible in either state.
  const footer = <CardFooterNote>{t.introDisclaimer}</CardFooterNote>

  return (
    <AccordionCard
      title={t.title}
      variant="sidebar"
      defaultOpen={defaultExpanded}
      collapsedContent={
        <>
          <Chips>
            {methods.map((m) => (
              <Chip key={m.key}>{m.chip}</Chip>
            ))}
          </Chips>
          {t.collapsedNote !== undefined ? (
            <CollapsedNote>{t.collapsedNote}</CollapsedNote>
          ) : null}
          {footer}
        </>
      }
    >
      <Body $variant="sidebar">
        <Section>
          <SectionTitle>{t.onlineTitle}</SectionTitle>
          <SectionBody>{t.onlineDesc}</SectionBody>
        </Section>
        {methods.map((m) => (
          <Section key={m.key}>
            <SectionTitle>
              <MethodIcon icon={m.icon} />
              {m.title}
            </SectionTitle>
            <SectionBody>{m.desc}</SectionBody>
            {m.note !== undefined ? <MethodNote>{m.note}</MethodNote> : null}
          </Section>
        ))}
        {children}
      </Body>
      {footer}
    </AccordionCard>
  )
}
